import React, { useMemo } from 'react';
import { fmt } from '../data/metrics';
import type { ConnectedSourceId, Outlet, SellItem } from '../types';
import { Icon } from './shared/Icon';

// Merge each outlet's per-item tally into a single fleet-wide list of the most
// popular pre-ordered sell-items, ranked by revenue.
function mergeItems(outlets: Outlet[]): SellItem[] {
  const map = new Map<string, SellItem>();
  for (const o of outlets) {
    for (const item of o.sellItems ?? []) {
      const key = `${item.name}__${item.emoji ?? ''}`;
      const prev = map.get(key);
      if (prev) {
        prev.qty += item.qty;
        prev.revenue += item.revenue;
      } else {
        map.set(key, { ...item });
      }
    }
  }
  return Array.from(map.values()).sort((a, b) => b.revenue - a.revenue);
}

export function SellItemsSection({
  outlets,
  connected,
  onDrill,
}: {
  outlets: Outlet[];
  connected: ConnectedSourceId[];
  onDrill?: () => void;
}) {
  const { items, places, totalRevenue } = useMemo(() => {
    const open = outlets.filter(o => !o.closed);
    const items = mergeItems(open).slice(0, 8);
    const places = open
      .filter(o => (o.sellRevenue ?? 0) > 0)
      .map(o => ({
        id: o.id,
        name: o.name,
        region: o.region,
        city: o.city,
        revenue: o.sellRevenue ?? 0,
        count: o.sellCount ?? 0,
      }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 8);
    const totalRevenue = open.reduce((a, o) => a + (o.sellRevenue ?? 0), 0);
    return { items, places, totalRevenue };
  }, [outlets]);

  // Only the venues that actually sell through the widget see this section.
  if (!connected.includes('alenopay') || (!items.length && !places.length)) {
    return null;
  }

  const maxItemRev = Math.max(...items.map(i => i.revenue), 0.01);
  const maxPlaceRev = Math.max(...places.map(p => p.revenue), 0.01);

  return (
    <section
      className={`sell-items${onDrill ? ' clickable' : ''}`}
      onClick={onDrill}
      role={onDrill ? 'button' : undefined}
    >
      <div className="sec-head">
        <h2>Sell items today</h2>
        <span className="sh-meta">
          €{Math.round(totalRevenue).toLocaleString('de-CH')} pre-ordered via the
          widget · click for full breakdown
        </span>
        <span
          className="prov-note"
          title="Built from priced reservation-widget answers (aleno Pay)."
        >
          aleno Pay
        </span>
      </div>
      <div className="sell-grid">
        <div className="ol-card">
          <div className="ol-head">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="star" size={16} style={{ color: 'var(--accent)' }} />
              <h3>Popular items</h3>
            </div>
            <span className="ol-conf">qty · revenue</span>
          </div>
          {items.length ? (
            <div className="si-list">
              {items.map(it => (
                <div className="si-row" key={`${it.name}-${it.emoji ?? ''}`}>
                  <div className="si-name">
                    <b>
                      {it.emoji ? `${it.emoji} ` : ''}
                      {it.name}
                    </b>
                    <span>{it.qty.toLocaleString('de-CH')} pre-ordered</span>
                  </div>
                  <div className="si-track">
                    <span
                      className="si-fill"
                      style={{ width: `${(it.revenue / maxItemRev) * 100}%` }}
                    />
                  </div>
                  <div className="si-val">{fmt.eur(it.revenue)}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="si-empty">No items pre-ordered yet today.</p>
          )}
        </div>

        <div className="ol-card">
          <div className="ol-head">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="pin" size={16} style={{ color: 'var(--accent)' }} />
              <h3>Where it sells</h3>
            </div>
            <span className="ol-conf">by outlet</span>
          </div>
          {places.length ? (
            <div className="si-list">
              {places.map(p => (
                <div className="si-row" key={p.id}>
                  <div className="si-name">
                    <b>{p.name}</b>
                    <span>
                      {p.region} · {p.city}
                    </span>
                  </div>
                  <div className="si-track">
                    <span
                      className="si-fill alt"
                      style={{ width: `${(p.revenue / maxPlaceRev) * 100}%` }}
                    />
                  </div>
                  <div className="si-val">{fmt.eur(p.revenue)}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="si-empty">No outlet revenue yet today.</p>
          )}
        </div>
      </div>
    </section>
  );
}
